import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import React, { useEffect, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { SpinnerContainer } from '@/components/Containers/SpinnerContainer';
import { ErrorModal } from '@/components/Elements/ErrorModal';
import { iFormContactFields } from '@/components/Form/FormContact';
import { iFormPhoneValidationFields } from '@/components/Form/FormPhoneValidation';
import { iFormTokenCheckFields } from '@/components/Form/FormTokenCheck';
import { iFormUserAccountFields } from '@/components/Form/FormUserAccount';
import { MyRatePrivatePage } from '@/components/PrivatePages/MyRatePrivatePage/MyRatePrivatePage';
import {
  APP_APPSUMO_URL,
  DEFAULT_MY_RATE_REFRESH_TIME_IN_SEG,
  NOTIFICATION_CHECKOUT_REMINDER_ID,
  NOTIFICATION_NEW_FREE_REMINDER_ID,
} from '@/config';
import {
  ACTIVE,
  CANCELLED,
  EUR,
  INTERVAL_COUNT_FOR_BILLING_MONTH,
  PENDING,
  PROCESSING,
} from '@/config/appConstants';
import { useUpdateBillingDetails } from '@/features/billing';
import { Country, getCountries } from '@/features/countries';
import { updateGroups } from '@/features/groups';
import { useCreateInvoicePending } from '@/features/invoices';
import { getMeInfo } from '@/features/me';
import {
  getMobileToken,
  MobileToken,
  useCreateMobileToken,
  useUpdateMobileToken,
} from '@/features/mobileToken';
import {
  getUserProducts,
  ResponseError,
  useDeleteUserPendingProduct,
  useReactivateUserCancelledProduct,
} from '@/features/myProducts';
import { useCreateNotificationContact } from '@/features/notification';
import {
  useCreatePayment,
  useDeleteUserIncompleteProduct,
  useUpdatePayment,
} from '@/features/payment';
import { useProducts } from '@/features/products';
import { getProvinces, Province } from '@/features/provinces';
import { getTinDocumentTypes, TinDocumentType } from '@/features/tinDocumentTypes';
import { useGroupBillingDetails } from '@/hooks/useGroupBillingDetails';
import { useMe } from '@/hooks/useMe';
import { useUserProducts } from '@/hooks/useUserProducts';
import { useAuth } from '@/lib/auth';
import { getAllAxiosData, GetterAxios } from '@/lib/axios';
import {
  buildGTMAddLogged,
  buildGTMCompletedBilling,
  buildGTMValidationAccount,
  sendEventGtm,
} from '@/lib/gtm/gtmUtils';
import { useNotificationStore } from '@/stores/notifications';
import { Currency, IntervalBillingMode, ProductTypes, UserProductTypes, Me } from '@/types';
import {
  buildProductsCheckingInStorageForPendings,
  checkByStatus,
  filterProducts,
  filterHiddenPriceProducts,
  filterUserProductsByStatus,
  generatePendingUserProductTypes,
  getBillingModeByIntervalCount,
  getMyRateURL,
  getProductFilterPricesByCurrencyBillingMode,
  getUserCurrency,
  getUserCurrentProductFromProducts,
  getUserData,
  isAdminUserFromMe,
  isCoreProduct,
  isCurrency,
  isFreemiumProduct,
  isIntervalBillingMode,
  isNewFreeUserFromMe,
  userHasProducts,
} from '@/utils/helpers';
import { PaymentIntentWrapper } from '@/utils/paymentIntentWrapper';
import storage from '@/utils/storage';

export type MyRatePageProps = {
  autoCheckout?: boolean;
  completeCheckout?: boolean;
};

const parseCurrency = (autoCheckout: boolean, currencyRaw: string | null): Currency | null => {
  if (!autoCheckout) return null;
  return isCurrency(currencyRaw as Currency) ? (currencyRaw as Currency) : null;
};

const parseInterval = (
  autoCheckout: boolean,
  intervalRaw: string | null,
  comIDRaw: string | null
): IntervalBillingMode | null => {
  if (!autoCheckout) return null;
  if (!intervalRaw && !comIDRaw) {
    return INTERVAL_COUNT_FOR_BILLING_MONTH;
  }

  if (intervalRaw && isIntervalBillingMode(intervalRaw)) {
    return parseInt(intervalRaw, 10) as IntervalBillingMode;
  }

  if (intervalRaw && !isIntervalBillingMode(intervalRaw)) {
    return INTERVAL_COUNT_FOR_BILLING_MONTH;
  }

  if (comIDRaw && isIntervalBillingMode(comIDRaw)) {
    return parseInt(comIDRaw, 10) as IntervalBillingMode;
  }

  return INTERVAL_COUNT_FOR_BILLING_MONTH;
};

const parseComID = (autoCheckout: boolean, comIDRaw: string | null): string | null => {
  if (!autoCheckout || isIntervalBillingMode(comIDRaw)) {
    return null;
  }

  return comIDRaw;
};

const parseSignature = (comID: string | null, signatureRaw: string | null): string | null => {
  if (!comID) return null;

  return signatureRaw;
};

const isAutoCheckoutAllowed = (
  products: ProductTypes[] | null | undefined,
  mdirectorId: string | null,
  currency: Currency | null,
  interval: IntervalBillingMode,
  userProducts: UserProductTypes[] | undefined,
  signature: string | null,
  me: Me
) => {
  if (!products || !mdirectorId || isNewFreeUserFromMe(me) || !currency) {
    return false;
  }

  const billingMode = getBillingModeByIntervalCount(interval);
  const checkByStatusResult = checkByStatus(
    userProducts,
    mdirectorId,
    [PENDING, ACTIVE, CANCELLED, PROCESSING],
    billingMode
  );
  if (!signature && checkByStatusResult) {
    return false;
  }

  const filteredProducts = signature
    ? products && filterProducts(products, mdirectorId)
    : products && filterHiddenPriceProducts(products, mdirectorId);
  if (
    !filteredProducts ||
    isFreemiumProduct(filteredProducts) ||
    (!userHasProducts(userProducts) && isCoreProduct(filteredProducts))
  ) {
    return false;
  }

  if (filteredProducts && !isCoreProduct(filteredProducts)) {
    return true;
  }
  return true;
};

export const MyRatePage = ({ autoCheckout = false, completeCheckout = false }: MyRatePageProps) => {
  const { setSelectedProductBillingMode, setSelectedProductCurrency } = useUserProducts();
  const { dismissNotification } = useNotificationStore();
  const { newLogin } = useAuth();
  const { getMe, setMe, isMeSet } = useMe();
  const { setBillingDetails, getBillingDetails, isBillingDetailsSet } = useGroupBillingDetails();

  const { i18n } = useTranslation();
  const { mdirectorId = null } = useParams();
  const {
    currency: currencyRaw = null,
    comID: comIDRaw = null,
    interval: intervalRaw = null,
    signature: signatureRaw = null,
  } = useParams();
  const currency = parseCurrency(autoCheckout, currencyRaw);
  const interval = parseInterval(autoCheckout, intervalRaw, comIDRaw);
  const comID = parseComID(autoCheckout, comIDRaw);
  const signature = parseSignature(comID, signatureRaw);

  const updateBillingDetailsMutation = useUpdateBillingDetails();
  const deleteUserPendingProductMutation = useDeleteUserPendingProduct();
  const reactivateUserCancelledProductMutation = useReactivateUserCancelledProduct();
  const createCreatePaymentMutation = useCreatePayment();
  const createUpdatePaymentMutation = useUpdatePayment();
  const createNotificationContactMutation = useCreateNotificationContact();
  const createMobileTokenMutation = useCreateMobileToken();
  const updateUpdateMobileTokenMutation = useUpdateMobileToken();
  const deleteUserIncompleteProductMutation = useDeleteUserIncompleteProduct();
  const createInvoicePendingMutation = useCreateInvoicePending();
  const handleError = useErrorHandler();
  const sendDataToGTM = useGTMDispatch();
  const [mobileToken, setMobileToken] = useState<MobileToken>();

  const groupId = getMe().groupInfo.id ? getMe().groupInfo.id : '';

  const [countries, setCountries] = useState<Country[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [tinDocumentTypes, setTinDocumentTypes] = useState<TinDocumentType[]>([]);
  const [autoCheckoutSetted, setAutoCheckoutSetted] = useState<boolean>(false);

  const [hasBeenCanceled, setHasBeenCanceled] = useState<boolean>(false);
  const [hasBeenAutoCanceled, setHasBeenAutoCanceled] = useState<boolean>(false);
  const [hasBeenReactivated, setHasBeenReactivated] = useState<boolean>(false);
  const [userProducts, setUserProducts] = useState<UserProductTypes[] | undefined>(undefined);
  const [seconds, setSeconds] = useState(0);

  const [userProductProcessing, setUserProductProcessing] = useState<ProductTypes | null>(null);
  const [statusDeleteUserPendingProductError, setStatusDeleteUserPendingProductError] = useState({
    status: 0,
    detail: '',
  });
  const productsQuery = useProducts();
  const { isLoading: productsIsLoading = false, data: products } = productsQuery;

  const billingDetails = getBillingDetails();

  const userCurrency = getUserCurrency(billingDetails, storage);

  useEffect(() => {
    if (!productsIsLoading && products && isBillingDetailsSet()) {
      const getters: GetterAxios[] = [
        { callback: getUserProducts, parameters: { groupId: groupId } },
        { callback: getCountries },
        { callback: getMobileToken, parameters: { groupId: groupId } },
        { callback: getProvinces, parameters: { countryId: billingDetails.countryId } },
        { callback: getTinDocumentTypes, parameters: { countryId: billingDetails.countryId } },
      ];
      const setters: any = [
        setUserProductsFilteringByStatus,
        setCountries,
        setMobileToken,
        setProvinces,
        setTinDocumentTypes,
      ];

      const pageData = getAllAxiosData(getters, setters);
      pageData.catch((error) => {
        handleError(error);
      });
    }
  }, [productsIsLoading, products, groupId, billingDetails, isBillingDetailsSet, handleError]);

  useEffect(() => {
    if (
      hasBeenCanceled ||
      hasBeenReactivated ||
      (userProductProcessing && seconds == DEFAULT_MY_RATE_REFRESH_TIME_IN_SEG)
    ) {
      const getters: GetterAxios[] = [
        { callback: getUserProducts, parameters: { groupId: groupId } },
      ];
      const setters: any = [setUserProductsFilteringByStatus];

      const pageData = getAllAxiosData(getters, setters);
      pageData.catch((error) => {
        handleError(error);
      });

      setSeconds(0);
    }
  }, [groupId, handleError, hasBeenCanceled, hasBeenReactivated, userProductProcessing, seconds]);

  useEffect(() => {
    const cancelPlan = async (product: ProductTypes | null | undefined) => {
      if (!product) {
        return;
      }
      storage.clearUserData();
      await deleteUserPendingProductMutation.mutateAsync({
        mdirectorId: product.mdirectorId,
        onErrorCallback: onDeleteUserPendingProductErrorCallback,
      });
      setHasBeenCanceled(true);
    };
    const cancelPendingProductIfNeeded = () => {
      const userProductPending = !autoCheckout && filterUserProductsByStatus(userProducts, PENDING);

      if (
        userProductPending &&
        userProductPending.product.hidePrice &&
        !hasBeenCanceled &&
        !hasBeenAutoCanceled
      ) {
        dismissNotification(
          `${NOTIFICATION_CHECKOUT_REMINDER_ID}${userProductPending.product?.mdirectorId}`
        );
        setHasBeenAutoCanceled(true);
        userProductPending &&
          userProductPending.product.hidePrice &&
          cancelPlan(userProductPending.product);
      }
    };
    cancelPendingProductIfNeeded();
  }, [
    userProducts,
    autoCheckout,
    hasBeenCanceled,
    hasBeenAutoCanceled,
    deleteUserPendingProductMutation,
    dismissNotification,
  ]);

  useEffect(() => {
    if (userProductProcessing) {
      const intervalUseEffect = setInterval(() => {
        setSeconds((seconds) => seconds + 1);
      }, 1000);
      return () => clearInterval(intervalUseEffect);
    }
  }, [userProductProcessing]);

  const onCancelIncompleteAction = async () => {
    storage.clearUserData();
    const userProducts = await deleteUserIncompleteProductMutation.mutateAsync({
      groupId: groupId,
    });
    setUserProducts(userProducts);
  };

  const setUserProductsFilteringByStatus = (userProducts: UserProductTypes[] | undefined) => {
    const pendingProduct = userProducts && filterUserProductsByStatus(userProducts, PENDING);

    if (!pendingProduct) {
      userProducts = buildProductsCheckingInStorageForPendings(userProducts, storage);
    }

    setUserProducts(userProducts);

    const processingProduct = userProducts && filterUserProductsByStatus(userProducts, PROCESSING);
    processingProduct && setUserProductProcessing(processingProduct.product);
  };

  const onRequestInfoSubmit = async (values: iFormContactFields) => {
    sendEventGtm(buildGTMAddLogged(userData), sendDataToGTM);

    await createNotificationContactMutation.mutateAsync({
      data: values,
    });
  };

  const onAppsumoActionlButton = () => {
    window.location.assign(APP_APPSUMO_URL);
  };

  const onPlanCancelButton = async (product: ProductTypes | null | undefined) => {
    if (!product) {
      return;
    }
    storage.clearUserData();
    dismissNotification(`${NOTIFICATION_CHECKOUT_REMINDER_ID}${product?.mdirectorId}`);
    await deleteUserPendingProductMutation.mutateAsync({
      mdirectorId: product.mdirectorId,
      onErrorCallback: onDeleteUserPendingProductErrorCallback,
    });

    setHasBeenCanceled(true);
  };

  const onPlanReactivateButton = async (product: ProductTypes | null | undefined) => {
    if (!product) {
      return;
    }

    storage.clearUserData();
    await reactivateUserCancelledProductMutation.mutateAsync({ mdirectorId: product.mdirectorId });
    setHasBeenReactivated(true);
  };

  const onSubmitFormPhoneValidation = async (values: iFormPhoneValidationFields) => {
    const mobileToken = await createMobileTokenMutation.mutateAsync({
      data: values,
      groupId: groupId,
    });

    setMobileToken(mobileToken);
  };

  const onSubmitFormTokenCheck = async (values: iFormTokenCheckFields) => {
    const mobileToken = await updateUpdateMobileTokenMutation.mutateAsync({
      data: values,
      groupId: groupId,
    });

    setMobileToken(mobileToken);
    billingDetails && setBillingDetails({ ...billingDetails, phone: values.phone });
  };

  const countryOnChange = (formValues: any, countryId: string) => {
    getProvinces({ countryId }).then((responseProvinces) => {
      const newBillingDetails = { ...billingDetails, ...formValues.values };
      newBillingDetails.countryId = countryId;
      if (
        !newBillingDetails?.companyCif ||
        !newBillingDetails?.companyCifDocumentType ||
        !newBillingDetails?.alreadySaved
      ) {
        newBillingDetails.alreadySaved = false;
      }

      getTinDocumentTypes({ countryId }).then((responseTinDocumentTypes) => {
        setBillingDetails(newBillingDetails);
        setProvinces(responseProvinces);
        setTinDocumentTypes(responseTinDocumentTypes);
      });
    });
  };

  const onSubmitFormUserAccount = async (values: iFormUserAccountFields): Promise<boolean> => {
    const updateBillingDetails = await updateBillingDetailsMutation.mutateAsync({
      data: values,
      groupId: groupId,
    });
    if (updateBillingDetails.hasError) {
      return false;
    }

    if (isNewFreeUserFromMe(getMe())) {
      !values.createdAt && sendEventGtm(buildGTMCompletedBilling(getMe()), sendDataToGTM);
      dismissNotification(NOTIFICATION_NEW_FREE_REMINDER_ID);
    }

    storage.clearUserCurrency();
    await newLogin(async () => true);
    await loadMe();
    if (isAdminUserFromMe(getMe()) && !values.createdAt) {
      sendEventGtm(buildGTMValidationAccount(getMe()), sendDataToGTM);
    }
    return true;
  };

  const onDeleteUserPendingProductErrorCallback = ({ status, detail }: ResponseError) => {
    setStatusDeleteUserPendingProductError({
      status,
      detail,
    });
  };

  const loadMe = async () => {
    const me = await getMeInfo();
    setMe(me);
    return true;
  };

  const unhideProducts = (userProductsFiltered: UserProductTypes[]) => {
    return userProductsFiltered.map((userProduct: UserProductTypes) => {
      userProduct.product.hidePrice = false;
      return userProduct;
    });
  };

  const addCheckoutProduct = (
    products: ProductTypes[] | null | undefined,
    mdirectorId: string | null,
    currency: Currency | null,
    userProducts: UserProductTypes[],
    interval: IntervalBillingMode,
    signature: string | null
  ): void => {
    const filteredProducts =
      products &&
      (signature
        ? filterProducts(products, mdirectorId)
        : filterHiddenPriceProducts(products, mdirectorId));
    if (!filteredProducts || !currency) return;

    const userProduct = getUserCurrentProductFromProducts(userProducts) as UserProductTypes;
    currency = (userProduct?.product?.prices[0]?.currency as Currency) || currency;

    let userProductsFiltered = userProducts.filter(
      (product: UserProductTypes) => product && product.groupProduct.status !== PENDING
    );

    const billingMode = getBillingModeByIntervalCount(interval);
    const productFiltered = getProductFilterPricesByCurrencyBillingMode(
      filteredProducts,
      currency,
      billingMode
    );

    if (productFiltered.prices.length === 0) {
      return;
    }

    userProductsFiltered.push(generatePendingUserProductTypes(productFiltered));

    if (signature && userProductsFiltered.length > 1) {
      userProductsFiltered = unhideProducts(userProductsFiltered);
    }

    setUserProducts(userProductsFiltered);
    setSelectedProductBillingMode(billingMode);
    setSelectedProductCurrency(currency);
    storage.setUserCurrency(currency as Currency);
  };

  const updateComId = (): void => {
    comID &&
      updateGroups({
        comID: comID,
        groupId: groupId,
      });
  };

  if (productsIsLoading || !userProducts || !isBillingDetailsSet() || !mobileToken || !isMeSet()) {
    return <SpinnerContainer />;
  }

  if (autoCheckout && !autoCheckoutSetted) {
    if (
      isAutoCheckoutAllowed(
        products,
        mdirectorId,
        currency,
        interval as IntervalBillingMode,
        userProducts,
        signature,
        getMe()
      )
    ) {
      addCheckoutProduct(
        products,
        mdirectorId,
        currency,
        userProducts,
        interval as IntervalBillingMode,
        signature
      );
      updateComId();
      setAutoCheckoutSetted(true);
      return <SpinnerContainer />;
    }
  }
  const userData = getUserData(getMe(), i18n);
  const paymentIntent = PaymentIntentWrapper({
    createCreatePaymentMutation,
    createUpdatePaymentMutation,
    setHasBeenReactivated,
    setHasBeenCanceled,
  });

  const onConfirmUnpaidMeteredErrorAction = async () => {
    await createInvoicePendingMutation.mutateAsync();
  };

  const renderStatusPaymentErrorModal = () => (
    <ErrorModal
      status={statusDeleteUserPendingProductError.status}
      detail={statusDeleteUserPendingProductError.detail}
      onRequestClose={() => {
        setStatusDeleteUserPendingProductError({ status: 0, detail: '' });
      }}
    />
  );

  return statusDeleteUserPendingProductError.status > 0 ? (
    renderStatusPaymentErrorModal()
  ) : (
    <MyRatePrivatePage
      userProducts={userProducts || []}
      userCurrency={userCurrency || EUR}
      userData={userData}
      onRequestInfoSubmit={onRequestInfoSubmit}
      onRequestInfo={() => null}
      countries={countries}
      provinces={provinces}
      tinDocumentTypes={tinDocumentTypes}
      countryOnChange={countryOnChange}
      onSubmitFormUserAccount={onSubmitFormUserAccount}
      onCancelButton={onPlanCancelButton}
      getCreatePaymentIntent={paymentIntent.getCreatePaymentIntent}
      getUpdatePaymentIntent={paymentIntent.getUpdatePaymentIntent}
      onFinishURLRedirect={getMyRateURL()}
      onPlanCancel={onPlanCancelButton}
      onPlanReactivate={onPlanReactivateButton}
      onAppsumoAction={onAppsumoActionlButton}
      autoCheckout={autoCheckout}
      mdirectorId={autoCheckout ? mdirectorId : ''}
      onSubmitFormPhoneValidation={onSubmitFormPhoneValidation}
      onSubmitFormTokenCheck={onSubmitFormTokenCheck}
      mobileToken={mobileToken}
      completeCheckout={completeCheckout}
      onCancelIncompleteAction={onCancelIncompleteAction}
      onConfirmUnpaidMeteredErrorAction={onConfirmUnpaidMeteredErrorAction}
    />
  );
};
